import { cropImageAPI } from '@/utils/api';
import { pickCroppedDisplayUrlFromRow, pickCroppedUrlFromRow } from './imageCropLoadResults';
import { collectCropOutputUrlsFromDetail } from './imageCropDetailPrints';
import { mapCropRowToDetailRow } from './imageCropRowStatus';

function readOriginalUrlFromRow(row) {
  return String(
    row?.originalUrl
    || row?.originalImageUrl
    || row?.original_image_url
    || row?.imageUrl
    || row?.url
    || '',
  ).trim();
}

/** 列表/历史预览元数据：优先裁剪结果，附带可直链展示的 display/view URL */
export function resolveCropListPreviewMeta(rows, task) {
  const list = Array.isArray(rows) ? rows : Object.values(rows || {});

  for (const row of list) {
    const url = pickCroppedUrlFromRow(row);
    if (!url) continue;
    const displayUrl = pickCroppedDisplayUrlFromRow(row) || url;
    return {
      url,
      displayUrl,
      viewUrl: displayUrl,
    };
  }

  for (const row of list) {
    const original = readOriginalUrlFromRow(row);
    if (original) {
      return { url: original, displayUrl: original, viewUrl: original };
    }
  }

  const fallback = resolveCropPreviewFromTaskResult(task);
  if (!fallback) {
    return { url: '', displayUrl: '', viewUrl: '' };
  }
  return { url: fallback, displayUrl: fallback, viewUrl: fallback };
}

/** 从 task.result 同步解析列表预览（无需请求 crop-images） */
export function resolveCropPreviewFromTaskResult(task) {
  if (!task) return '';
  const result = task.result && typeof task.result === 'object' ? task.result : {};
  if (Array.isArray(result.outputs)) {
    for (const row of result.outputs) {
      if (row?.status === 'failed') continue;
      const url = row?.url || row?.outputUrl || row?.imageUrl || row?.croppedUrl || '';
      if (String(url).trim()) return String(url).trim();
    }
  }
  const candidates = [
    result.previewUrl,
    result.croppedImageUrl,
    result.cropped_image_url,
    result.imageUrl,
    result.image_url,
    result.originalUrl,
    result.original_url,
    task.previewUrl,
    task.croppedImageUrl,
  ];
  for (const candidate of candidates) {
    if (candidate && String(candidate).trim()) return String(candidate).trim();
  }
  return '';
}

/** 历史列表是否已有可用预览（避免重复拉 batch） */
export function hasResolvedCropHistoryPreview(task) {
  return Boolean(String(task?.previewUrl || task?._outputPreview || '').trim());
}

/** 判断历史列表预览字段是否与 meta 一致 */
export function cropHistoryPreviewMatches(task, meta) {
  const url = String(meta?.url || '').trim();
  if (!url) return true;
  const viewUrl = String(meta?.viewUrl || url).trim();
  const displayUrl = String(meta?.displayUrl || viewUrl).trim();
  return (
    String(task?.previewUrl || '').trim() === url
    && String(task?._outputPreview || '').trim() === url
    && String(task?.previewViewUrl || '').trim() === viewUrl
    && String(task?.previewDisplayUrl || '').trim() === displayUrl
  );
}

/** 从 crop-images 行解析列表预览，必要时回退 task.result */
export function resolveCropPreviewFromCropImages(rows, task) {
  const list = Array.isArray(rows) ? rows : Object.values(rows || {});
  for (const row of list) {
    const cropped = pickCroppedUrlFromRow(row);
    if (cropped) return cropped;
  }
  for (const row of list) {
    const original = readOriginalUrlFromRow(row);
    if (original) return original;
  }
  return resolveCropPreviewFromTaskResult(task);
}

function normalizeCropBatchRows(batchRes) {
  if (Array.isArray(batchRes?.images)) return batchRes.images;
  if (Array.isArray(batchRes?.data)) return batchRes.data;
  return Object.values(batchRes?.images || batchRes?.data || {});
}

/** 历史列表预览只需首张可用图，缩小单次 batch 载荷 */
export const CROP_HISTORY_PREVIEW_BATCH_PAGE_SIZE = 8;

/** 可视行预览拉取并发上限，避免打开历史时整页打满 */
export const CROP_HISTORY_PREVIEW_FETCH_CONCURRENCY = 2;

/**
 * 拉取裁剪任务历史行预览元数据（小 pageSize）。
 * @param {object} task
 * @returns {Promise<{ url: string, displayUrl: string, viewUrl: string }>}
 */
export async function fetchCropHistoryPreviewMeta(task) {
  const tid = String(task?.id || task?.taskId || '').trim();
  if (!tid) {
    return { url: '', displayUrl: '', viewUrl: '' };
  }
  const batchRes = await cropImageAPI.listBatchPage(tid, {
    page: 1,
    pageSize: CROP_HISTORY_PREVIEW_BATCH_PAGE_SIZE,
  });
  return resolveCropListPreviewMeta(normalizeCropBatchRows(batchRes), task);
}

/**
 * 有界并发映射；mapper 返回值收集为数组（与 items 同序，失败为 null）。
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} mapper
 * @returns {Promise<(R|null)[]>}
 */
export async function mapWithConcurrency(items, concurrency, mapper) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return [];
  const limit = Math.max(1, Math.min(Number(concurrency) || 1, list.length));
  const results = new Array(list.length).fill(null);
  let cursor = 0;
  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;
      try {
        results[index] = await mapper(list[index], index);
      } catch {
        results[index] = null;
      }
    }
  };
  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
}

function cropGalleryUrlLooksDirectlyLoadable(url) {
  const value = String(url || '').trim();
  if (!value) return false;
  if (value.startsWith('data:') || value.startsWith('blob:')) return true;
  if (value.startsWith('http://') || value.startsWith('https://')) return true;
  if (value.includes('/api/storage/images/view')) return true;
  if (value.startsWith('/images/') || value.startsWith('/assets/')) return true;
  return false;
}

/** Studio 结果区：优先 display/view，再回退 canonical url */
export function resolveCropStudioGalleryUrls(finalResult, fallbackUrls = []) {
  const outputs = Array.isArray(finalResult?.outputs) ? finalResult.outputs : [];
  const fromOutputs = outputs
    .filter((row) => row && row.status !== 'failed')
    .map((row) => String(
      row.displayUrl
      || row.viewUrl
      || row.outputUrl
      || row.url
      || row.imageUrl
      || '',
    ).trim())
    .filter(Boolean);
  if (fromOutputs.length > 0) return fromOutputs;
  return (Array.isArray(fallbackUrls) ? fallbackUrls : [])
    .map((url) => String(url || '').trim())
    .filter(Boolean);
}

/** 从 crop-images 行展开全部裁剪结果展示 URL（多印花时多张） */
export function resolveCropGalleryUrlsFromRows(rows, sources = []) {
  const list = Array.isArray(rows) ? rows : [];
  const urls = [];
  list.forEach((row, index) => {
    const imageId = String(row?.imageId ?? row?.image_id ?? sources[index]?.id ?? '').trim();
    if (!imageId) return;
    const originalUrl = String(
      row?.originalImageUrl
      ?? row?.original_image_url
      ?? sources[index]?.url
      ?? '',
    ).trim();
    const detail = mapCropRowToDetailRow(imageId, row, originalUrl);
    urls.push(...collectCropOutputUrlsFromDetail(detail));
  });
  return [...new Set(urls.map((url) => String(url || '').trim()).filter(Boolean))];
}

/**
 * Studio 上传区裁剪结果预览：与历史记录一致，优先可直链 view/display URL。
 */
export async function fetchCropStudioGalleryDisplayUrls({
  taskId,
  sources = [],
  finalResult,
  fallbackUrls = [],
} = {}) {
  const fromResult = resolveCropStudioGalleryUrls(finalResult, fallbackUrls);
  if (fromResult.length > 0 && fromResult.every(cropGalleryUrlLooksDirectlyLoadable)) {
    return fromResult;
  }

  const tid = String(taskId || '').trim();
  if (!tid) return fromResult.length ? fromResult : fallbackUrls;

  try {
    const batchRes = await cropImageAPI.listBatchPage(tid, { page: 1, pageSize: 100 });
    const fromBatch = resolveCropGalleryUrlsFromRows(normalizeCropBatchRows(batchRes), sources);
    if (fromBatch.length > 0) return fromBatch;
  } catch (error) {
    console.warn('[图案裁剪 Studio] 结果预览拉取失败', tid, error);
  }

  return fromResult.length ? fromResult : fallbackUrls;
}
